import { API_BASE_URL } from '../config/api';

import { Feedback, CreateFeedbackRequest, UpdateFeedbackRequest, CreateFeedbackReportRequest } from '../types/artist';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export const feedbackApi = {
  async getFeedbackByTrackId(trackId: number): Promise<Feedback[]> {
    try {
      console.log('Getting feedback for track ID:', trackId);
      console.log('API URL:', `${API_BASE_URL}/api/Feedback/track/${trackId}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Feedback/track/${trackId}`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get feedback response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get feedback response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get feedback failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get feedback');
      }

      return data;
    } catch (error) {
      console.error('Get feedback error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async getFeedbackById(id: number): Promise<Feedback> {
    try {
      console.log('Getting feedback by ID:', id);
      console.log('API URL:', `${API_BASE_URL}/api/Feedback/${id}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Feedback/${id}`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get feedback response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get feedback response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get feedback failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get feedback');
      }

      return data;
    } catch (error) {
      console.error('Get feedback error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async createFeedback(feedbackData: CreateFeedbackRequest): Promise<Feedback> {
    try {
      console.log('Creating feedback with data:', feedbackData);
      console.log('API URL:', `${API_BASE_URL}/api/Feedback`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Feedback`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      console.log('Create feedback response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Create feedback response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Create feedback failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to create feedback');
      }

      return data;
    } catch (error) {
      console.error('Create feedback error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async updateFeedback(id: number, feedbackData: UpdateFeedbackRequest): Promise<void> {
    try {
      console.log('Updating feedback with data:', feedbackData);
      console.log('API URL:', `${API_BASE_URL}/api/Feedback/${id}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Feedback/${id}`, {
        method: 'PUT',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(feedbackData),
      });

      console.log('Update feedback response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to update feedback';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Update feedback failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Update feedback error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  async deleteFeedback(id: number): Promise<void> {
    try {
      console.log('Deleting feedback with ID:', id);
      console.log('API URL:', `${API_BASE_URL}/api/Feedback/${id}`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Feedback/${id}`, {
        method: 'DELETE',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Delete feedback response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to delete feedback';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Delete feedback failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Delete feedback error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  // Get all feedback for tracks owned by the current user
  async getMyTracksFeedback(): Promise<Feedback[]> {
    try {
      console.log('Getting feedback for my tracks');
      console.log('API URL:', `${API_BASE_URL}/api/Feedback/my-tracks`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Feedback/my-tracks`, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Get my tracks feedback response status:', response.status);

      let data;
      try {
        data = await response.json();
        console.log('Get my tracks feedback response data:', data);
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new ApiError(response.status, 'Invalid response format from server');
      }

      if (!response.ok) {
        console.error('Get my tracks feedback failed with status:', response.status);
        throw new ApiError(response.status, data.message || 'Failed to get feedback');
      }

      return data;
    } catch (error) {
      console.error('Get my tracks feedback error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },

  // Report feedback for content violation
  async reportFeedback(feedbackId: number, reportData: CreateFeedbackReportRequest): Promise<void> {
    try {
      console.log('Reporting feedback with ID:', feedbackId);
      console.log('API URL:', `${API_BASE_URL}/api/Feedback/${feedbackId}/report`);
      
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/Feedback/${feedbackId}/report`, {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData),
      });

      console.log('Report feedback response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to report feedback';
        try {
          const data = await response.json();
          errorMessage = data.message || errorMessage;
        } catch (jsonError) {
          // If we can't parse JSON, use default message
        }
        console.error('Report feedback failed with status:', response.status);
        throw new ApiError(response.status, errorMessage);
      }
    } catch (error) {
      console.error('Report feedback error:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(0, 'Network error occurred: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  },
};